import { useEffect, useRef, useState, useCallback } from 'react'
import { withStyles } from '@aura/components/styles'
import Button from '@aura/components/button'
import Form from '@aura/forms/form'
import { useTranslation } from 'react-i18next'
import Grid from '@aura/components/grid'
import Box from '@aura/components/box'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import Typography from '@aura/components/typography'
import HorizontalGroup from '@aura/components/horizontal-group'
import PropTypes from 'prop-types'
import RegistrationContainer from '../../../../components/registration-container-new'
import formFields, { initialValues } from './constants/form-fields'
import styles from './styles'


const OTP_LENGTH = 6
const Component = ({
    classes,
    authVerified,
    error,
    isBackAllowed,
    isOptionalMfa,
    isProcessing,
    onBack,
    onNext,
    resetAuthVerified,
    title,
    verbiage
}) => {

    const ref = useRef()
    const inputRefs = useRef([])
    const { t: translate, i18n } = useTranslation()
    const [ otp, setOtp ] = useState([ '', '', '', '', '', '' ])
    const [ hasError, setHasError ] = useState(false)
    const [ nextButtonDisabled, setNextButtonDisabled ] = useState(true)

    useEffect(() => {
        resetAuthVerified && resetAuthVerified()
    }, [])

    useEffect(() => {
        const { setFieldError } = ref.current.getContext()
        error && setFieldError(PASSCODE, translate('oktaApiResponse.' + `${error}`))
    }, [ error ])

    useEffect(() => {
        const { setFieldError, resetForm } = ref.current.getContext()
        setFieldError(PASSCODE, null)
        resetForm()
    }, [ i18n.language ])


    useEffect(() => {
        const otpValue = otp.join('')
        setNextButtonDisabled(otpValue.length !== OTP_LENGTH)
    }, [ otp ])

    const getFirstEmptyIndex = useCallback(() => {
        return otp.findIndex(digit => digit === '')
    }, [ otp ])

    const handleOtpChange = useCallback((index, value) => {
        // Only allow numbers
        if (value && !/^\d$/.test(value)) {
            return
        }
        const firstEmptyIndex = getFirstEmptyIndex()

        if (value && firstEmptyIndex !== -1 && index !== firstEmptyIndex) {
            const newOtp = [ ...otp ]
            newOtp[firstEmptyIndex] = value
            setOtp(newOtp)
            setHasError(false)
            if (firstEmptyIndex < otp.length - 1) {
                setTimeout(() => {
                    inputRefs.current[firstEmptyIndex + 1]?.focus()
                }, 0)
            }
            return
        }

        const newOtp = [ ...otp ]
        newOtp[index] = value
        setOtp(newOtp)
        setHasError(false)
        

        if (value && index < otp.length - 1) {
            setTimeout(() => {
                inputRefs.current[index + 1]?.focus()
            }, 0)
        }
        
    }, [ otp, getFirstEmptyIndex ])
    
    const handleOtpKeyDown = useCallback((index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus()
        }
        if (e.key === 'Enter') {
            const otpValue = otp.join('')
            if (otpValue.length === otp.length && !isProcessing) {
                onNext({ passCode: otpValue })
            }
        }
    }, [ otp ])
    
    const handleOtpPaste = useCallback(e => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
        if (pastedData) {
            const newOtp = [ ...otp ]
            for (let i = 0; i < pastedData.length && i < 6; i++) {
                newOtp[i] = pastedData[i]
            }
            setOtp(newOtp)
            // Focus the next empty field or the last field
            const nextIndex = Math.min(pastedData.length, 5)
            inputRefs.current[nextIndex]?.focus()
        }
    }, [ otp ])

    const { PASSCODE } = formFields

    const validate = values => {
        const errors = {}

        if (!values[PASSCODE]) {
            errors[PASSCODE] = translate('required')
        }

        console.log('errors', errors)
        return errors
    }

    const footerContent = ( 
        <Grid className={classes.buttonContainer} container item md={12} xs={12}>
            <Grid container className={classes.actionRow} justify="flex-end">
                <HorizontalGroup gutter={10}>
                    {isBackAllowed
                        ? <Button
                            className={classes.buttonBack}
                            onClick={onBack}
                            variant="text"
                            disabled={isProcessing}>{translate('back')}
                        </Button>
                        : <></>
                    }
                    <Button
                        className={classes.buttonSubmit}
                        onClick={() => onNext({ passCode: otp.join('') })}
                        variant="contained"
                        disabled={isProcessing || nextButtonDisabled}>{translate('next')}
                    </Button>
                </HorizontalGroup>
            </Grid>
        </Grid>

    )

    const stepVerbiage = <div>{translate('loginFlow.authenticatorCode.stepVerbiage', { verbiage: verbiage })}</div>
    const stepTitle = !isOptionalMfa ? translate(`authenticatorOptions.optionalMfaTitle.${title}`) : translate(`authenticatorOptions.${title}`)

    return (
        <RegistrationContainer {...{ footerActions: !authVerified ? footerContent : null, title: stepTitle, verbiage: stepVerbiage }}>
            <Form className={classes.formContainer} {...{
                enableReinitialize: true,
                initialValues: initialValues,
                onSubmit: onNext,
                ref,
                validate
            }}>
                <div className={classes.fieldsContainer}>
                    <Grid className={classes.authenticatorCodeContainer} container spacing={2}>
                        <Box className={classes.authenticatorCodeBox}>
                            <Typography variant="body2" className={classes.otpLabel}>
                                {translate('loginFlow.accountRecovery.verificationCode')}
                            </Typography>
                            <Box display="flex" gap={1} justifyContent="center">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={el => inputRefs.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => handleOtpChange(index, e.target.value)}
                                        onKeyDown={e => handleOtpKeyDown(index, e)}
                                        onFocus={() => {
                                            const firstEmpty = otp.findIndex(d => d === '')
                                            if (firstEmpty !== -1 && index !== firstEmpty) {
                                                setTimeout(() => {
                                                    inputRefs.current[firstEmpty]?.focus()
                                                }, 0)
                                            }
                                        }}
                                        onPaste={handleOtpPaste}
                                        autoFocus={index === 0}
                                        className={`${classes.otpInput} ${hasError ? classes.otpInputError : ''} ${digit ? classes.otpInputFilled : ''} ${authVerified  ? classes.otpInputVerified : ''}`}
                                        aria-label={`Digit ${index + 1}`}
                                    />
                                ))}
                            </Box>

                        </Box>
                        {error && !authVerified && (
                            <Box className={classes.errorContainer} mt={2}>
                                <InfoOutlineIcon color='error' fontSize="small" />
                                <Typography variant="caption" color="error" className={classes.errorText}>
                                    {translate('oktaApiResponse.' + `${error}`)}
                                </Typography>
                            </Box>
                        )}
                        {authVerified && (
                            <Box className={classes.successContainer}>
                                <CheckCircleOutlineIcon className={classes.checkCircle} color='success' />
                                <Typography variant="caption" color="success" className={classes.successMsg}>
                                    {translate('loginFlow.accountRecovery.verified')}
                                </Typography>
                            </Box>)}
                    </Grid>
                    <br />

                </div>
                <Box className={classes.warningLine}>{translate('mfaEnrollment.verbiage07')} </Box>
            </Form>
        </RegistrationContainer>
    )
}

Component.displayName = 'AuthenticatorCode'

Component.defaultProps = {
    title: ''
}

Component.propTypes = {
    classes: PropTypes.shape({
        button: PropTypes.object,
        loader: PropTypes.object,
        root: PropTypes.object
    }),
    data: PropTypes.object,
    error: PropTypes.string,
    isBackAllowed: PropTypes.bool,
    onBack: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    title: PropTypes.string,
    verbiage: PropTypes.string
}

export default withStyles(Component, styles)
